import { CodeVerifier } from '@/stores/auth/accessors'
import { base64URLEncode, generateNonce, sha256, str2ab } from '@/stores/auth/functions'
import getEnv from '@/util/env'

const providerUrl = (): string => {
  const configured = getEnv('VITE_OIDC_PROVIDER_URL')
  if (!configured) {
    throw new Error('VITE_OIDC_PROVIDER_URL is not configured')
  }
  return configured.replace(/\/+$/, '')
}

const endpoint = (path: string): string =>
  `${providerUrl()}/protocol/openid-connect/${path}`

/**
 * Scopes requested at the authorization endpoint.
 *
 * `organization` is an *optional* client scope in Keycloak, so it is only granted
 * when asked for by name here — a default scope would arrive without being
 * listed. It carries the organization claim used to tell a user's own tenant from
 * the others.
 *
 * Add new scopes here rather than at the token endpoint: the authorization_code
 * grant has no scope parameter (RFC 6749 §4.1.3), so the scope is fixed by the
 * authorization request that issued the code.
 */
export const OIDC_SCOPES = 'openid organization'

export const generateTokenEndpoint = (): string => endpoint('token')

const generateCodeChallengeData = async () => {
  const state = await generateNonce()
  const codeVerifier = await generateNonce()
  const codeChallengeInput = await sha256(str2ab(codeVerifier))
  const code_challenge = base64URLEncode(codeChallengeInput)
  CodeVerifier.set(state, codeVerifier)
  return {
    state,
    code_challenge_method: 'S256',
    code_challenge,
    response_type: 'code',
    scope: OIDC_SCOPES
  }
}

export const generateLoginUrl = async (): Promise<string> => {
  const codeParams = await generateCodeChallengeData()
  const urlParams = {
    client_id: getEnv('VITE_OIDC_CLIENT_ID'),
    redirect_uri: `${window.location.origin}/redirect`,
    ...codeParams,
  }
  const urlParamsString = new URLSearchParams(urlParams).toString()
  return `${endpoint('auth')}?${urlParamsString}`
}

export const generateLogoutUrl = async (id_token: string | null): Promise<string> => {
  const urlParams = {
    client_id: getEnv('VITE_OIDC_CLIENT_ID'),
    post_logout_redirect_uri: `${window.location.origin}/redirect`,
    state: await generateNonce(),
    // Left out rather than sent empty: the provider treats the two the same, and
    // an empty hint reads as though one was meant to be there.
    ...(id_token ? { id_token_hint: id_token } : {}),
  }
  const urlParamsString = new URLSearchParams(urlParams).toString()
  return `${endpoint('logout')}?${urlParamsString}`
}
